import { useState, useCallback } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';
import { useRouter } from 'next/router';
import { useMarketPageFilters } from '@/creditflowresearch/application/Deal/useMarketPageFilters';
import { IMarketPagePayload } from '@/creditflowresearch/application/Deal/types';
import { ICorporateMarketDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';

const INITIAL_PAGE = 1;
const BUFFER_PER_PAGE = 20;
const LOAD_MORE_PER_PAGE = 10;
const INITIAL_VISIBLE_COUNT = 10;

export interface ICorporateMarketPageDealsCollection {
  totalCount: number,
  collection: ICorporateMarketDeal[],
  pageFilters?: IMarketPagePayload | null,
}

interface IUseMarketPageDealsProps {
  deals: ICorporateMarketPageDealsCollection,
  fetch?: (payload: IMarketPagePayload) => Promise<ICorporateMarketPageDealsCollection>,
}

const useCorporateMarketPageDeals = ({
  deals,
  fetch,
}: IUseMarketPageDealsProps) => {
  const { getErrorMessage } = useMessageManager();
  const {
    collection = [],
    totalCount: initialTotalCount,
    pageFilters,
  } = deals;

  const { buildFilters, buildPaging, selectedStatuses } = useMarketPageFilters({ pageFilters });
  const { query } = useRouter();
  const { products, regions, markets, segments } = query;

  const [dealsCollection, setDealsCollection] = useState<ICorporateMarketDeal[]>(
    collection.slice(0, INITIAL_VISIBLE_COUNT),
  );
  const [bufferCollection, setBufferCollection] = useState<ICorporateMarketDeal[]>(
    collection.slice(INITIAL_VISIBLE_COUNT),
  );
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE + 1);

  const hasBufferedItems = bufferCollection.length > 0;

  const buildPayload = useCallback(
    (page: number, perPage: number): IMarketPagePayload => {
      const filters = buildFilters();
      const paging = buildPaging();
      return {
        filters,
        paging: {
          ...paging,
          page,
          perPage,
        },
      };
    },
    [buildFilters, buildPaging],
  );

  const fetchDeals = useCallback(
    async (payloadToFetch: IMarketPagePayload) => {
      if (!fetch) {
        return;
      }

      try {
        setIsFetching(true);
        const response = await fetch(payloadToFetch);
        setTotalCount(response?.totalCount);

        if (payloadToFetch.paging.page === INITIAL_PAGE) {
          setDealsCollection(response?.collection.slice(0, INITIAL_VISIBLE_COUNT));
          setBufferCollection(response?.collection.slice(INITIAL_VISIBLE_COUNT));
          setCurrentPage(INITIAL_PAGE + 1);
        } else {
          setDealsCollection((prev) => [...prev, ...response.collection]);
          setCurrentPage(payloadToFetch.paging.page);
        }
      } catch (error) {
        AlertManager.error(getErrorMessage(error));
      } finally {
        setIsFetching(false);
      }
    },
    [fetch],
  );

  useAutoFetch(() => {
    const payload = buildPayload(INITIAL_PAGE, BUFFER_PER_PAGE);
    fetchDeals(payload);
  }, [products, regions, markets, segments, selectedStatuses], !!collection.length);

  const loadMore = useCallback(() => {
    if (hasBufferedItems) {
      setDealsCollection((prev) => [...prev, ...bufferCollection]);
      setBufferCollection([]);
      return;
    }

    const nextPage = currentPage + 1;
    const payload = buildPayload(nextPage, LOAD_MORE_PER_PAGE);
    fetchDeals(payload);
  }, [hasBufferedItems, bufferCollection, currentPage, buildPayload, fetchDeals]);

  return {
    isFetching,
    collection: dealsCollection,
    totalCount,
    loadMore,
    fetchDeals,
    pageFilters,
  };
};

export default useCorporateMarketPageDeals;
